<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <div>
        <div class="font-bold text-14">{{ translations.title }}</div>
        <div class="mt-1 text-12">
          {{ translations.file_description }}
        </div>
      </div>
      <div class="ml-auto"></div>
      <div><slot></slot></div>
    </div>
    <uploader-error
      :translations="translations"
      class="mb-4"
      v-if="onError"
    ></uploader-error>
    <div
      class="
        flex
        items-center
        justify-center
        py-12
        border-2 border-dashed
        rounded-lg
        cursor-pointer
        bg-secondary-100
        drop-container
        hover-bg-primary-200 hover-text-primary hover-border-primary
        focus-visible:ring
      "
      tabindex="0"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
      @click.stop="clickSection"
    >
      <input
        type="file"
        class="block w-0 overflow-hidden opacity-0"
        ref="file"
        :accept="accept.join(',')"
        @change="onChange"
        :aria-label="translations.upload_link"
      />
      <div
        class="flex items-center space-x-2 pr-11"
        :class="{ 'text-primary': isHovering, 'border-r': !isHovering }"
      >
        <upload-arrow class="h-8" />
        <span class="text-14" :class="`text-${isHovering ? 'link' : 'faded'}`">
          {{
            isHovering ? translations.upload : translations.uploader_description
          }}
        </span>
      </div>
      <label v-show="!isHovering" class="cursor-pointer">
        <div class="underline text-14">{{ translations.upload_link }}</div>
      </label>
    </div>
    <div v-if="!!file && showSuccess && !onError">
      <uploader-success
        :name="file.name"
        :preview-image="previewImage"
        @edit="editFile"
        @delete="deleteFile"
      ></uploader-success>
    </div>
  </div>
</template>

<script>
import UploadArrow from "@/assets/upload-arrow.svg";
import UploaderSuccess from "@/components/General/UploaderSuccess.vue";
import UploaderError from "@/components/General/UploaderError.vue";
import { getImageSize } from "@/utilities/files";
export default {
  name: "Uploader",
  props: {
    imageLibraryOptions: Object,
    accept: {
      type: Array,
      default() {
        return [".jpg", ".jpeg", ".png", ".bmp", ".gif"];
      },
    },
    maxSize: {
      type: Number,
      default: 5000000,
    },
    minWidth: {
      type: Number,
    },
    maxWidth: {
      type: Number,
    },
    minHeight: {
      type: Number,
    },
    maxHeight: {
      type: Number,
    },
    showSuccess: {
      type: Boolean,
      default: true,
    },
    translations: {
      type: Object,
      default() {
        return {
          title: "Uploading a file",
          file_description:
            "Supported format (jpg, jpeg, gif, bmp, png). Maximum (10mb).",
          uploader_description: "Drag a file to upload",
          upload: "Upload",
          upload_link: "Select file",
          error_title: "Attention! Problems have been found.",
          error_max: "Exceeds max limit",
          error_size: "Poor quality image",
          error_format: "Non-supported format",
        };
      },
    },
  },
  data() {
    return {
      isHovering: false,
      file: null,
      previewImage: null,
      sizeError: false,
      typeError: false,
      dimentionsError: false,
    };
  },
  computed: {
    onError() {
      return this.sizeError || this.typeError || this.dimentionsError;
    },
  },
  methods: {
    clickSection() {
      this.$refs.file.click();
    },
    deleteFile() {
      this.$emit("delete");
      this.file = null;
      this.previewImage = null;
      this.sizeError = false;
      this.typeError = false;
      this.dimentionsError = false;
    },
    editFile() {
      this.$emit("edit", this.file);
    },
    dragover(event) {
      event.preventDefault();
      this.isHovering = true;
      this.handleClassesToggle(event);
    },
    dragleave(event) {
      this.isHovering = false;
      this.handleClassesToggle(event);
    },
    drop(event) {
      event.preventDefault();
      this.$refs.file.files = event.dataTransfer.files;
      this.onChange();
      this.isHovering = false;
      this.handleClassesToggle(event);
    },
    onChange() {
      this.file = this.$refs.file.files[0];
      this.validate();
    },
    publishChanges() {
      if (!this.onError) {
        this.$emit("files", [this.file]);
        this.$emit("error", false);

        if (this.file.type.includes("image") && this.showSuccess) {
          this.previewImage = URL.createObjectURL(this.file);
        }
      } else {
        this.$emit("files", null);
        this.$emit("error", true);
      }
    },
    validate() {
      this.validateExtention();
      this.validateSize();
      if (this.file.type.includes("image")) {
        this.validateWidthHeight();
      } else {
        this.publishChanges();
      }
    },
    async validateWidthHeight() {
      const size = await getImageSize(URL.createObjectURL(this.file));
      if (
        size.width < this.minWidth ||
        size.width > this.maxWidth ||
        size.height < this.minHeight ||
        size.height > this.maxHeight
      ) {
        this.dimentionsError = true;
      } else {
        this.dimentionsError = false;
      }
      this.publishChanges();
    },
    validateSize() {
      if (this.file.size > this.maxSize) {
        this.sizeError = true;
      } else {
        this.sizeError = false;
      }
    },
    validateExtention() {
      const ext = `.${this.file.name.toLowerCase().split(".").pop()}`;
      if (this.accept.indexOf(ext) === -1) {
        this.typeError = true;
      } else {
        this.typeError = false;
      }
    },
    handleClassesToggle(event) {
      if (this.isHovering) {
        event.currentTarget.classList.add(
          "bg-primary-200",
          "text-primary",
          "border-primary",
          "hover"
        );
        return;
      }

      event.currentTarget.classList.remove(
        "bg-primary-200",
        "text-primary",
        "border-primary",
        "hover"
      );
    },
  },
  components: {
    UploadArrow,
    UploaderSuccess,
    UploaderError,
  },
};
</script>
<style lang="scss" scoped>
@import "@/styles/tailwind.scss";
.mtm {
  .drop-container:hover,
  .drop-container:focus {
    border-color: $exMtm;
    color: $exMtm;
    background-color: $exMtmBgLightGreen;
  }
}
</style>
